import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  ExternalLink,
  Gauge,
  Info,
  ShieldAlert,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Badge,
  Card,
  Button as ShButton,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/index.js';
import { CopyButton } from '../../components/ui/index.js';

import { OTP_BASE_URL, OTP_DOCS_URL } from './_shared/OtpShared.js';

interface EndpointDoc {
  id: string;
  method: 'GET' | 'POST';
  path: string;
  title: string;
  desc: string;
  headers: Array<{ name: string; value: string }>;
  body?: Array<{ field: string; desc: string }>;
  success: string;
  curl: string;
}

interface ErrorDoc {
  http: string;
  code: string;
  meaning: string;
}

/**
 * Static in-dashboard developer documentation for the public OTP API — what
 * the operator forwards to customers alongside the interactive Swagger docs.
 * All technical literals (paths, headers, JSON, curl) stay untranslated and
 * LTR; only the prose is localised.
 */
export function PlatformOtpDocsPage(): JSX.Element {
  const { t } = useTranslation('platform');

  const authHeader = { name: 'Authorization', value: 'Bearer otpsk_YOUR_KEY' };
  const jsonHeader = { name: 'Content-Type', value: 'application/json' };

  const endpoints: EndpointDoc[] = [
    {
      id: 'send',
      method: 'POST',
      path: '/otp/send',
      title: t('otpService.docs.send.title', { defaultValue: 'Send a code' }),
      desc: t('otpService.docs.send.desc', {
        defaultValue:
          'Generates a one-time code and delivers it to the phone over WhatsApp. Re-sending to the same phone invalidates the previous code.',
      }),
      headers: [authHeader, jsonHeader],
      body: [
        {
          field: 'phone',
          desc: t('otpService.docs.send.phoneDesc', {
            defaultValue:
              'Destination phone in international format, digits only (6–20). "+", spaces, and dashes are stripped automatically.',
          }),
        },
      ],
      success: '{ "sent": true, "ttlSeconds": 300 }',
      curl: [
        `curl -X POST ${OTP_BASE_URL}/otp/send \\`,
        '  -H "Authorization: Bearer otpsk_YOUR_KEY" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"phone":"9647501234567"}'`,
      ].join('\n'),
    },
    {
      id: 'verify',
      method: 'POST',
      path: '/otp/verify',
      title: t('otpService.docs.verify.title', { defaultValue: 'Verify a code' }),
      desc: t('otpService.docs.verify.desc', {
        defaultValue:
          'Checks the code the user typed. A code is single-use — after a successful verify it is consumed and cannot be replayed.',
      }),
      headers: [authHeader, jsonHeader],
      body: [
        {
          field: 'phone',
          desc: t('otpService.docs.verify.phoneDesc', {
            defaultValue: 'The same phone the code was sent to.',
          }),
        },
        {
          field: 'code',
          desc: t('otpService.docs.verify.codeDesc', {
            defaultValue: 'The code the user received (4–8 digits).',
          }),
        },
      ],
      success: '{ "valid": true }',
      curl: [
        `curl -X POST ${OTP_BASE_URL}/otp/verify \\`,
        '  -H "Authorization: Bearer otpsk_YOUR_KEY" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"phone":"9647501234567","code":"123456"}'`,
      ].join('\n'),
    },
    {
      id: 'message',
      method: 'POST',
      path: '/messages/send',
      title: t('otpService.docs.message.title', { defaultValue: 'Send a message' }),
      desc: t('otpService.docs.message.desc', {
        defaultValue:
          'Sends an ordinary WhatsApp text from the account\u2019s connected number \u2014 no code, no template. Billed exactly like an OTP send: one message at the account price, charged only when the gateway accepts it. "sent": true means accepted, not delivered \u2014 there are no delivery or read receipts and no provider message id. The body is never stored. Requires messaging to be enabled on the account, otherwise 403 otp_service.messaging_disabled.',
      }),
      headers: [authHeader, jsonHeader],
      body: [
        {
          field: 'phone',
          desc: t('otpService.docs.message.phoneDesc', {
            defaultValue:
              'Destination phone in international format, digits only (6\u201320). "+", spaces, and dashes are stripped automatically.',
          }),
        },
        {
          field: 'message',
          desc: t('otpService.docs.message.messageDesc', {
            defaultValue:
              'The text to send. Trimmed, then 1\u20131000 characters; newlines are kept. One request sends to one recipient \u2014 there is no bulk endpoint.',
          }),
        },
      ],
      success: '{ "sent": true }',
      curl: [
        `curl -X POST ${OTP_BASE_URL}/messages/send \\`,
        '  -H "Authorization: Bearer otpsk_YOUR_KEY" \\',
        '  -H "Content-Type: application/json" \\',
        `  -d '{"phone":"9647501234567","message":"Your order #1042 has shipped."}'`,
      ].join('\n'),
    },
    {
      id: 'messages',
      method: 'GET',
      path: '/messages',
      title: t('otpService.docs.messages.title', { defaultValue: 'Message log' }),
      desc: t('otpService.docs.messages.desc', {
        defaultValue:
          'Every message that left the account\u2019s WhatsApp number, newest first \u2014 not only the ones this API sent. Metering attributes a message to the number it went out from, so the log also holds api rows (OTP codes), erp rows (a storefront login code off the same number) and test rows (an operator test send); they all appear because they are all billed. Filter with page, pageSize (max 100), status (sent | failed) and source (api | erp | test | message). The recipient is shown in full only for the account\u2019s own api and message rows \u2014 third-party numbers are masked to their last 4 digits and phoneMasked is true. There is no message body: it is never stored. Failures carry a stable errorCode (rejected_by_whatsapp | gateway_unreachable | delivery_failed) instead of the gateway\u2019s own words.',
      }),
      headers: [authHeader],
      success: [
        '{',
        '  "items": [{',
        '    "id": "0f4b1d2e-8a91-4c3b-9f77-2b1c5d0e7a44",',
        '    "phone": "9647501234567", "phoneMasked": false,',
        '    "source": "message", "status": "sent", "errorCode": null,',
        '    "billable": true, "unitPrice": "0.0100",',
        '    "createdAt": "2026-08-18T09:14:02.000Z"',
        '  }],',
        '  "total": 184, "page": 1, "pageSize": 25',
        '}',
      ].join('\n'),
      curl: [
        `curl "${OTP_BASE_URL}/messages?source=message&pageSize=25" \\`,
        '  -H "Authorization: Bearer otpsk_YOUR_KEY"',
      ].join('\n'),
    },
    {
      id: 'usage',
      method: 'GET',
      path: '/usage',
      title: t('otpService.docs.usage.title', { defaultValue: 'Usage & billing' }),
      desc: t('otpService.docs.usage.desc', {
        defaultValue:
          'Returns send/fail counts and cost for the window, plus your all-time payments and balance. Money is returned as 4-decimal strings. Optional from / to query parameters (YYYY-MM-DD) scope the window (cost only); the default is the last 30 days. paymentsTotal and balance are always all-time (balance = all-time cost − payments; negative = credit).',
      }),
      headers: [authHeader],
      success: [
        '{',
        '  "from": "2026-06-19", "to": "2026-07-19",',
        '  "totalSent": 1240, "totalFailed": 3,',
        '  "cost": "12.4000", "paymentsTotal": "50.0000", "balance": "7.6000",',
        '  "pricePerMessage": "0.0100",',
        '  "days": [{ "date": "2026-07-19", "sent": 41, "failed": 0 }]',
        '}',
      ].join('\n'),
      curl: [
        `curl ${OTP_BASE_URL}/usage \\`,
        '  -H "Authorization: Bearer otpsk_YOUR_KEY"',
      ].join('\n'),
    },
  ];

  const errors: ErrorDoc[] = [
    {
      http: '401',
      code: 'otp_service.invalid_key',
      meaning: t('otpService.docs.errors.invalidKey', {
        defaultValue: 'Missing, malformed, or revoked API key.',
      }),
    },
    {
      http: '403',
      code: 'otp_service.client_disabled',
      meaning: t('otpService.docs.errors.clientDisabled', {
        defaultValue: 'The account is disabled — contact the provider.',
      }),
    },
    {
      http: '400',
      code: 'validation.failed',
      meaning: t('otpService.docs.errors.validationFailed', {
        defaultValue:
          'The request body or query is invalid — e.g. a message that is blank or over 1000 characters, or a phone that is not 6–20 digits. Note this is 400, not 422.',
      }),
    },
    {
      http: '403',
      code: 'otp_service.messaging_disabled',
      meaning: t('otpService.docs.errors.messagingDisabled', {
        defaultValue:
          'General messaging is not enabled on this account yet (OTP still works). Turn it on from the client page.',
      }),
    },
    {
      http: '422',
      code: 'otp_service.not_configured',
      meaning: t('otpService.docs.errors.notConfigured', {
        defaultValue: 'No WhatsApp instance is configured for this account yet.',
      }),
    },
    {
      http: '422',
      code: 'otp_service.delivery_failed',
      meaning: t('otpService.docs.errors.deliveryFailed', {
        defaultValue: 'WhatsApp could not deliver the message (not billed).',
      }),
    },
    {
      http: '422',
      code: 'otp_service.invalid_code',
      meaning: t('otpService.docs.errors.invalidCode', {
        defaultValue: 'The code does not match.',
      }),
    },
    {
      http: '422',
      code: 'otp_service.code_expired',
      meaning: t('otpService.docs.errors.codeExpired', {
        defaultValue: 'The code has expired — request a new one.',
      }),
    },
    {
      http: '422',
      code: 'otp_service.too_many_attempts',
      meaning: t('otpService.docs.errors.tooManyAttempts', {
        defaultValue: 'Too many wrong attempts — request a new code.',
      }),
    },
    {
      http: '429',
      code: 'otp_service.cooldown',
      meaning: t('otpService.docs.errors.cooldown', {
        defaultValue: 'A code was just sent to this phone — wait 45 seconds between sends.',
      }),
    },
  ];

  const limits: string[] = [
    t('otpService.docs.limits.sendKey', { defaultValue: 'Send: 120 requests / minute per key.' }),
    t('otpService.docs.limits.sendPhone', {
      defaultValue: 'Send: 6 requests / hour per phone, plus a 45-second cooldown between sends.',
    }),
    t('otpService.docs.limits.verifyKey', { defaultValue: 'Verify: 240 requests / minute per key.' }),
    t('otpService.docs.limits.verifyPhone', {
      defaultValue: 'Verify: 15 attempts / 15 minutes per phone.',
    }),
    t('otpService.docs.limits.messageKey', {
      defaultValue: 'Messages: 15 requests / minute per key (cause.code otp_service.rate_limited_burst).',
    }),
    t('otpService.docs.limits.messagePhone', {
      defaultValue: 'Messages: 10 / hour per recipient (otp_service.rate_limited_recipient).',
    }),
    t('otpService.docs.limits.messageNumber', {
      defaultValue:
        'Messages: 150 / hour and 400 / day per connected WhatsApp number — the number, not the account, because that is what WhatsApp bans (otp_service.rate_limited_hourly / _daily).',
    }),
    t('otpService.docs.limits.messageList', {
      defaultValue: 'Message log: 120 requests / minute per key.',
    }),
  ];

  const notes: string[] = [
    t('otpService.docs.notes.expiry', {
      defaultValue: 'Codes expire after ttlSeconds (configured per account).',
    }),
    t('otpService.docs.notes.resend', {
      defaultValue: 'Re-sending to a phone invalidates the previous code.',
    }),
    t('otpService.docs.notes.singleUse', {
      defaultValue: 'Verification is single-use — a verified code cannot be replayed.',
    }),
    t('otpService.docs.notes.billing', {
      defaultValue: 'Billing is per successful send; failed deliveries are free.',
    }),
    t('otpService.docs.notes.messageOptIn', {
      defaultValue:
        'General messaging is opt-in per account and off by default — enable it on the client page. It is also the messaging-only kill switch: turning it off leaves OTP traffic running.',
    }),
    t('otpService.docs.notes.messageBilling', {
      defaultValue:
        'General messages are billed at the same per-message price as a code, on gateway acceptance only. "sent": true means accepted — nothing here tracks delivery or read receipts.',
    }),
    t('otpService.docs.notes.noBodyStored', {
      defaultValue:
        'Message text is never stored, so the message log shows recipient, status and price only — never the content.',
    }),
    t('otpService.docs.notes.usageNotSplit', {
      defaultValue:
        'GET /usage does not split OTP from general traffic (the price is identical) — use GET /messages?source=... for the breakdown.',
    }),
  ];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/platform/otp-service"
          className="inline-flex items-center gap-1 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline"
        >
          <ArrowLeft className="h-3 w-3 rtl:rotate-180" aria-hidden />
          {t('otpService.title', { defaultValue: 'OTP service' })}
        </Link>
      </div>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-warning/15 text-warning">
            <BookOpen className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {t('otpService.docs.title', { defaultValue: 'OTP API documentation' })}
            </h1>
            <p className="text-sm text-fg-muted">
              {t('otpService.docs.subtitle', {
                defaultValue:
                  'Everything a customer needs to integrate — base URL, authentication, endpoints, errors, and limits.',
              })}
            </p>
          </div>
        </div>
        <a href={OTP_DOCS_URL} target="_blank" rel="noreferrer">
          <ShButton variant="outline">
            <ExternalLink className="h-4 w-4" />
            {t('otpService.docs.openInteractive', { defaultValue: 'Open interactive docs' })}
          </ShButton>
        </a>
      </header>

      {/* Base URL + auth */}
      <Card className="p-6">
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-fg-muted">
              {t('otpService.docs.baseUrl', { defaultValue: 'Base URL' })}
            </span>
            <code dir="ltr" className="rounded border border-border bg-bg-subtle px-2 py-1 font-mono text-xs">
              {OTP_BASE_URL}
            </code>
            <CopyButton value={OTP_BASE_URL} subject={t('otpService.docs.baseUrl', { defaultValue: 'Base URL' })} />
          </div>
          <div>
            <h2 className="text-sm font-semibold">
              {t('otpService.docs.authTitle', { defaultValue: 'Authentication' })}
            </h2>
            <p className="mt-1 text-sm text-fg-muted">
              {t('otpService.docs.authHint', {
                defaultValue: 'Send the API key with every request in the Authorization header:',
              })}
            </p>
            <div className="mt-2 flex w-fit items-center gap-1">
              <code dir="ltr" className="rounded border border-border bg-bg-subtle px-2 py-1 font-mono text-xs">
                Authorization: Bearer otpsk_...
              </code>
              <CopyButton value={`${authHeader.name}: ${authHeader.value}`} subject={t('otpService.docs.authTitle', { defaultValue: 'Authentication' })} />
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-fg">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
            <span>
              {t('otpService.docs.serverSideWarning', {
                defaultValue:
                  'The API key is a server-side secret. Never ship it in a mobile app, browser code, or a public repository — anyone holding it can send messages billed to the account.',
              })}
            </span>
          </div>
        </div>
      </Card>

      {endpoints.map((ep) => (
        <Card key={ep.id} className="p-6">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={ep.method === 'GET' ? 'info' : 'success'}>{ep.method}</Badge>
            <code dir="ltr" className="font-mono text-sm font-semibold">{ep.path}</code>
            <CopyButton value={`${OTP_BASE_URL}${ep.path}`} subject={ep.path} />
            <span className="text-sm text-fg-muted">— {ep.title}</span>
          </div>
          <p className="mt-2 text-sm text-fg-muted">{ep.desc}</p>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                  {t('otpService.docs.headers', { defaultValue: 'Headers' })}
                </h3>
                <ul className="mt-2 flex flex-col gap-1">
                  {ep.headers.map((h) => (
                    <li key={h.name} className="flex items-center gap-1">
                      <code dir="ltr" className="rounded bg-bg-subtle px-2 py-0.5 font-mono text-xs">
                        {h.name}: {h.value}
                      </code>
                      <CopyButton value={`${h.name}: ${h.value}`} subject={h.name} />
                    </li>
                  ))}
                </ul>
              </div>
              {ep.body && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                    {t('otpService.docs.body', { defaultValue: 'Request body' })}
                  </h3>
                  <dl className="mt-2 flex flex-col gap-1.5 text-sm">
                    {ep.body.map((f) => (
                      <div key={f.field} className="flex items-start gap-2">
                        <dt>
                          <code dir="ltr" className="rounded bg-bg-subtle px-1.5 py-0.5 font-mono text-xs">
                            {f.field}
                          </code>
                        </dt>
                        <dd className="text-xs text-fg-muted">{f.desc}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                    {t('otpService.docs.success', { defaultValue: 'Success response' })}
                  </h3>
                  <CopyButton value={ep.success} subject={t('otpService.docs.success', { defaultValue: 'Success response' })} />
                </div>
                <pre dir="ltr" className="mt-2 overflow-x-auto rounded-md border border-border bg-bg-subtle/40 p-3 text-start font-mono text-xs">
                  {ep.success}
                </pre>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                  {t('otpService.docs.curl', { defaultValue: 'Example' })}
                </h3>
                <CopyButton value={ep.curl} label={t('otpService.docs.copy', { defaultValue: 'Copy' })} subject={t('otpService.docs.curl', { defaultValue: 'Example' })} />
              </div>
              <pre dir="ltr" className="mt-2 overflow-x-auto rounded-md border border-border bg-bg-subtle/40 p-3 text-start font-mono text-xs">
                {ep.curl}
              </pre>
            </div>
          </div>
        </Card>
      ))}

      {/* Errors */}
      <Card className="overflow-hidden p-0">
        <div className="flex items-start gap-3 p-6 pb-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-warning" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('otpService.docs.errorsTitle', { defaultValue: 'Errors' })}
            </h2>
            <p className="mt-1 text-xs text-fg-muted">
              {t('otpService.docs.errorsHint', {
                defaultValue:
                  'Failures return a JSON body carrying cause.code — match on the code, not the message text.',
              })}
            </p>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('otpService.docs.http', { defaultValue: 'HTTP' })}</TableHead>
              <TableHead>{t('otpService.docs.code', { defaultValue: 'cause.code' })}</TableHead>
              <TableHead>{t('otpService.docs.meaning', { defaultValue: 'Meaning' })}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {errors.map((e) => (
              <TableRow key={e.code}>
                <TableCell className="font-mono text-xs tabular-nums">{e.http}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <code dir="ltr" className="font-mono text-xs">{e.code}</code>
                    <CopyButton value={e.code} subject={t('otpService.docs.code', { defaultValue: 'cause.code' })} />
                  </div>
                </TableCell>
                <TableCell className="text-xs text-fg-muted">{e.meaning}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Rate limits */}
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <Gauge className="mt-0.5 h-5 w-5 text-info" aria-hidden />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('otpService.docs.limitsTitle', { defaultValue: 'Rate limits' })}
            </h2>
          </div>
          <ul className="mt-3 flex list-disc flex-col gap-1.5 ps-5 text-sm text-fg-muted">
            {limits.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </Card>

        {/* Notes */}
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 text-info" aria-hidden />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              {t('otpService.docs.notesTitle', { defaultValue: 'Notes' })}
            </h2>
          </div>
          <ul className="mt-3 flex list-disc flex-col gap-1.5 ps-5 text-sm text-fg-muted">
            {notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
